import { compressToUTF16, decompressFromUTF16 } from 'lz-string';

const daysjs = require('../utils/dayjsConfig');
const LZ_PREFIX = '__LZ__:';
// Single cache key for the distributions tree. Versioned internally via a
// `version` field in the persisted payload, not via a suffix in the key name;
// the schema guard on read checks that `version` field.
const DISTRIBUTIONS_CACHE_KEYS = ['DISTRIBUTIONS_DATA'];
const shouldCompressKey = key => DISTRIBUTIONS_CACHE_KEYS.includes(key);

// Keys that should use IndexedDB instead of localStorage (for large data)
const INDEXEDDB_KEYS = DISTRIBUTIONS_CACHE_KEYS;
const shouldUseIndexedDB = key => INDEXEDDB_KEYS.includes(key);

// IndexedDB configuration
const INDEXEDDB_NAME = 'sciroot_storage';
const INDEXEDDB_VERSION = 1;
const INDEXEDDB_STORE = 'large_data';

/**
 * Check if IndexedDB is available
 */
const isIndexedDBAvailable = () => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (e) {
    return false;
  }
};

/**
 * Open IndexedDB connection
 */
const openIndexedDB = () => {
  return new Promise((resolve, reject) => {
    if (!isIndexedDBAvailable()) {
      reject(new Error('IndexedDB not available'));
      return;
    }

    const request = indexedDB.open(INDEXEDDB_NAME, INDEXEDDB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(INDEXEDDB_STORE)) {
        db.createObjectStore(INDEXEDDB_STORE, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Get item from IndexedDB
 */
const IndexedDB_getItem = async itemKey => {
  try {
    const db = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(INDEXEDDB_STORE, 'readonly');
      const store = transaction.objectStore(INDEXEDDB_STORE);
      const request = store.get(itemKey);

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

      request.onsuccess = () => {
        db.close();
        const result = request.result;
        resolve(result ? result.value : null);
      };
    });
  } catch (error) {
    console.log('[IndexedDB] Get error:', error.message);
    return null;
  }
};

/**
 * Set item in IndexedDB
 */
const IndexedDB_setItem = async (itemKey, itemValue) => {
  try {
    const db = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(INDEXEDDB_STORE, 'readwrite');
      const store = transaction.objectStore(INDEXEDDB_STORE);
      const request = store.put({ key: itemKey, value: itemValue });

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

      request.onsuccess = () => {
        db.close();
        resolve();
      };
    });
  } catch (error) {
    console.log('[IndexedDB] Set error:', error.message);
  }
};

/**
 * Remove item from IndexedDB
 */
const IndexedDB_removeItem = async itemKey => {
  try {
    const db = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(INDEXEDDB_STORE, 'readwrite');
      const store = transaction.objectStore(INDEXEDDB_STORE);
      const request = store.delete(itemKey);

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

      request.onsuccess = () => {
        db.close();
        resolve();
      };
    });
  } catch (error) {
    console.log('[IndexedDB] Remove error:', error.message);
  }
};

/**
 * Migrate data from localStorage to IndexedDB (one-time migration)
 */
const migrateToIndexedDB = async itemKey => {
  try {
    if (!isLocalStorageAvailable() || !isIndexedDBAvailable()) {
      return;
    }

    const localValue = localStorage.getItem(itemKey);
    if (localValue) {
      console.log(
        `[IndexedDB] Migrating ${itemKey} from localStorage to IndexedDB`
      );
      await IndexedDB_setItem(itemKey, localValue);
      localStorage.removeItem(itemKey);
      console.log(`[IndexedDB] Migration complete for ${itemKey}`);
    }
  } catch (error) {
    console.log('[IndexedDB] Migration error:', error.message);
  }
};

const compressValue = value => {
  if (typeof value !== 'string') {
    return value;
  }

  // Skip compression for large data (over 500KB)
  // lz-string has a property limit of ~196607 which can be hit with large JSON objects
  if (value.length > 500 * 1024) {
    return value;
  }

  try {
    const compressed = compressToUTF16(value);
    if (typeof compressed === 'string' && compressed.length) {
      return `${LZ_PREFIX}${compressed}`;
    }
    return value;
  } catch (error) {
    console.log('Compression error', error);
    return value;
  }
};

const decompressValue = value => {
  if (typeof value !== 'string' || !value.startsWith(LZ_PREFIX)) {
    return value;
  }
  try {
    const decompressed = decompressFromUTF16(value.slice(LZ_PREFIX.length));
    return typeof decompressed === 'string' ? decompressed : value;
  } catch (error) {
    console.log('Decompression error', error);
    return value;
  }
};

var asyncStorage = null;

// PERFORMANCE: In-memory cache for frequently accessed keys
// This avoids slow AsyncStorage reads for selected entity IDs
// Cache entries have a TTL to prevent serving stale data
const memoryCache = new Map();
const CACHE_TTL = 5000; // 5 seconds TTL to prevent stale data
const CACHED_KEYS = [
  'SELECTED_DISTRIBUTION_ID',
  'SELECTED_FARMING_UNIT_ID',
  'SELECTED_FIELD_ID',
  'SELECTED_SENSOR_TRANSMITTER_IDS',
];

export const isLocalStorageAvailable = () => {
  try {
    return typeof localStorage === 'object';
  } catch (e) {
    return false;
  }
};

//Used only by Mobile-App
export var initStorage = storage => {
  asyncStorage = storage;
};

// Android's SQLite CursorWindow can't read a row bigger than ~2MB (the
// sCursorWindowSize reflection hack is unreliable on newer devices), so large
// values (e.g. DISTRIBUTIONS_DATA, which grows past 10MB) fail to read with
// "Row too big to fit into CursorWindow". To stay under that limit we split big
// values across several small rows: each chunk lives at `${key}__chunk__${i}`
// and the main key holds a tiny manifest (`__CHUNKED__:<count>`).
const CHUNK_THRESHOLD = 1024 * 1024; // values larger than ~1MB get chunked
const CHUNK_SIZE = 512 * 1024; // ~512KB per row, comfortably under the window
const CHUNK_MARKER = '__CHUNKED__:';
const chunkKey = (itemKey, i) => `${itemKey}__chunk__${i}`;

const parseChunkManifest = stored =>
  typeof stored === 'string' && stored.startsWith(CHUNK_MARKER)
    ? parseInt(stored.slice(CHUNK_MARKER.length), 10) || null
    : null;

async function safeRawGet(itemKey) {
  try {
    return await asyncStorage.getItem(itemKey);
  } catch (e) {
    // A pre-existing oversized row throws here — treat as absent so the next
    // write can overwrite it with a chunked manifest.
    return null;
  }
}

async function removeChunks(itemKey, total) {
  const removals = [];
  for (let i = 0; i < total; i++) {
    removals.push(
      asyncStorage.removeItem(chunkKey(itemKey, i)).catch(() => {})
    );
  }
  await Promise.all(removals);
}

async function writeChunked(itemKey, value) {
  const total = Math.ceil(value.length / CHUNK_SIZE);
  const writes = [];
  for (let i = 0; i < total; i++) {
    writes.push(
      asyncStorage.setItem(
        chunkKey(itemKey, i),
        value.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
      )
    );
  }
  await Promise.all(writes);
  // Write the manifest last so a partial write never leaves a readable-but-
  // incomplete value (until it's set, the main key keeps its previous value).
  await asyncStorage.setItem(itemKey, `${CHUNK_MARKER}${total}`);
}

async function readChunked(itemKey, total) {
  const reads = [];
  for (let i = 0; i < total; i++) {
    reads.push(asyncStorage.getItem(chunkKey(itemKey, i)));
  }
  const parts = await Promise.all(reads);
  for (let i = 0; i < total; i++) {
    if (parts[i] == null) {
      throw new Error(`Missing chunk ${i}/${total} for ${itemKey}`);
    }
  }
  return parts.join('');
}

async function AsyncStorage_getItem(itemKey) {
  try {
    // PERFORMANCE: Check memory cache first for frequently accessed keys
    if (CACHED_KEYS.includes(itemKey) && memoryCache.has(itemKey)) {
      const cacheEntry = memoryCache.get(itemKey);
      if (Date.now() - cacheEntry.timestamp < CACHE_TTL) {
        return cacheEntry.value;
      }
      memoryCache.delete(itemKey);
    }

    let item = await asyncStorage.getItem(itemKey);

    // Reassemble chunked values written to dodge the CursorWindow limit.
    const chunkCount = parseChunkManifest(item);
    if (chunkCount != null) {
      item = await readChunked(itemKey, chunkCount);
    }

    if (shouldCompressKey(itemKey)) {
      item = decompressValue(item);
    }

    // PERFORMANCE: Cache the result for frequently accessed keys with timestamp
    if (CACHED_KEYS.includes(itemKey)) {
      memoryCache.set(itemKey, { value: item, timestamp: Date.now() });
    }

    return item;
  } catch (e) {
    if (itemKey.includes('TOKEN') || itemKey === 'USER_DETAILS') {
      console.error('[AUTH] AsyncStorage get FAILED:', {
        key: itemKey,
        error: e.message,
      });
    }
    return undefined;
  }
}

export const getItem = (itemKey, defaultValue = undefined) => {
  // Web: Use IndexedDB for large data keys
  if (isLocalStorageAvailable() && shouldUseIndexedDB(itemKey)) {
    return (async () => {
      // First, migrate any existing localStorage data to IndexedDB
      await migrateToIndexedDB(itemKey);

      const storedValue = await IndexedDB_getItem(itemKey);
      if (storedValue === null || storedValue === undefined) {
        return defaultValue === undefined ? storedValue : defaultValue;
      }
      if (shouldCompressKey(itemKey)) {
        return decompressValue(storedValue);
      }
      return storedValue;
    })();
  }

  // Web: Use localStorage for other keys
  if (isLocalStorageAvailable()) {
    const storedValue = localStorage.getItem(itemKey);
    if (storedValue === null || storedValue === undefined) {
      return defaultValue === undefined ? storedValue : defaultValue;
    }
    if (shouldCompressKey(itemKey)) {
      return decompressValue(storedValue);
    }
    return storedValue;
  }

  // Mobile: Use AsyncStorage
  if (asyncStorage && itemKey != 'SELECTED_SENSOR_TRANSMITTER_IDS') {
    return AsyncStorage_getItem(itemKey);
  }

  return defaultValue;
};

async function AsyncStorage_removeItem(itemKey) {
  try {
    if (CACHED_KEYS.includes(itemKey)) {
      memoryCache.delete(itemKey);
    }
    const chunkCount = parseChunkManifest(await safeRawGet(itemKey));
    if (chunkCount != null) {
      await removeChunks(itemKey, chunkCount);
    }
    await asyncStorage.removeItem(itemKey);
  } catch (e) {
    // Ignore
  }
}

export var removeItem = itemKey => {
  // Web: Use IndexedDB for large data keys
  if (isLocalStorageAvailable() && shouldUseIndexedDB(itemKey)) {
    return IndexedDB_removeItem(itemKey);
  }

  // Web: Use localStorage for other keys
  if (isLocalStorageAvailable()) {
    localStorage.removeItem(itemKey);
    return;
  }

  // Mobile: Use AsyncStorage
  if (asyncStorage && itemKey != 'SELECTED_SENSOR_TRANSMITTER_IDS') {
    return AsyncStorage_removeItem(itemKey);
  }
};

async function AsyncStorage_setItem(itemKey, itemValue) {
  try {
    if (!itemValue) {
      itemValue = '';
    }

    const preparedValue =
      shouldCompressKey(itemKey) && typeof itemValue === 'string'
        ? compressValue(itemValue)
        : itemValue;

    // Clean up chunks from a previous large write before re-writing this key.
    const prevChunks = parseChunkManifest(await safeRawGet(itemKey));
    if (prevChunks != null) {
      await removeChunks(itemKey, prevChunks);
    }

    if (
      typeof preparedValue === 'string' &&
      preparedValue.length > CHUNK_THRESHOLD
    ) {
      await writeChunked(itemKey, preparedValue);
    } else {
      await asyncStorage.setItem(itemKey, preparedValue);
    }

    if (CACHED_KEYS.includes(itemKey)) {
      memoryCache.set(itemKey, { value: itemValue, timestamp: Date.now() });
    }
  } catch (e) {
    if (itemKey.includes('TOKEN') || itemKey === 'USER_DETAILS') {
      console.error('[AUTH] AsyncStorage save FAILED:', {
        key: itemKey,
        error: e.message,
      });
    }
    if (CACHED_KEYS.includes(itemKey)) {
      memoryCache.delete(itemKey);
    }
  }
}

export var setItem = (itemKey, itemValue) => {
  // Web: Use IndexedDB for large data keys (no quota issues)
  if (isLocalStorageAvailable() && shouldUseIndexedDB(itemKey)) {
    return (async () => {
      try {
        const preparedValue =
          shouldCompressKey(itemKey) && typeof itemValue === 'string'
            ? compressValue(itemValue)
            : itemValue;
        await IndexedDB_setItem(itemKey, preparedValue);
      } catch (e) {
        console.log('[IndexedDB] Set error:', e);
      }
    })();
  }

  // Web: Use localStorage for other keys
  if (isLocalStorageAvailable()) {
    try {
      const preparedValue =
        shouldCompressKey(itemKey) && typeof itemValue === 'string'
          ? compressValue(itemValue)
          : itemValue;
      localStorage.setItem(itemKey, preparedValue);
    } catch (e) {
      console.log('Local storage error', e);
    }
    return;
  }

  // Mobile: Use AsyncStorage
  if (asyncStorage && itemKey != 'SELECTED_SENSOR_TRANSMITTER_IDS') {
    AsyncStorage_setItem(itemKey, itemValue);
  }
};

export var getAllStorageKeys = async () => {
  if (asyncStorage) {
    return await asyncStorage.getAllKeys();
  }
};

export var multiGetFromStorage = async keys => {
  if (asyncStorage) {
    const results = await asyncStorage.multiGet(keys);
    return results.map(result => {
      const key = result[0];
      const value = shouldCompressKey(key)
        ? decompressValue(result[1])
        : result[1];
      return [key, value];
    });
  }
};

export const loadFailedRequestsFromStorage = async () => {
  try {
    const now = daysjs();
    const keys = await getAllStorageKeys();
    let filteredKeys = keys.filter(key => {
      let requestTs = daysjs.unix(key.replace('failed_sensor_request_', ''));
      let keyIsRequest = key.includes('failed_sensor_request');
      let diff = now.diff(requestTs, 'weeks');
      return keyIsRequest && diff < 3;
    });
    const result = await multiGetFromStorage(filteredKeys);
    const requests = result.map(req => JSON.parse(req[1]));
    return { requests: requests, keys: filteredKeys };
  } catch (error) {
    console.error(error);
  }
};

export const loadTokensToReduxSync = dispatch => {
  const id_token = localStorage.getItem('ID_TOKEN', '');
  const access_token = localStorage.getItem('ACCESS_TOKEN', '');
  const refresh_token = localStorage.getItem('REFRESH_TOKEN', '');

  if (
    id_token &&
    access_token &&
    refresh_token &&
    typeof dispatch === 'function'
  ) {
    const authenticateActions = require('@actions/authenticateActions');
    dispatch(
      authenticateActions.refresh_token_success(
        id_token,
        access_token,
        refresh_token
      )
    );
  }
};

export const loadTokensToReduxAsync = async dispatch => {
  try {
    const id_token = await asyncStorage.getItem('ID_TOKEN');
    const access_token = await asyncStorage.getItem('ACCESS_TOKEN');
    const refresh_token = await asyncStorage.getItem('REFRESH_TOKEN');

    if (
      id_token &&
      access_token &&
      refresh_token &&
      typeof dispatch === 'function'
    ) {
      const authenticateActions = require('@actions/authenticateActions');
      dispatch(
        authenticateActions.refresh_token_success(
          id_token,
          access_token,
          refresh_token
        )
      );
    }
  } catch (error) {
    console.error('[AUTH] loadTokensToReduxAsync error:', error);
  }
};
